import React from 'react';
import styled from 'styled-components';
import GridTableRow, { GridTableRowProps, TableHeaderId } from './GridTableRow';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { RequestState } from '../../auth/hooks/useRequestState';

const StyledTable = styled.table`
  width: 100%;
  height: 100%;
  overflow-x: scroll;
  background-color: ${COLORS.whiteBase};
  border-bottom: 1px solid ${COLORS.grayLight};
  tr {
    display: table-row;
    width: 100%;
    position: relative;
    padding: 0;
    svg {
      margin-right: ${SPACERS.tiny};
    }
  }
  thead tr,
  thead tr th {
    background-color: ${COLORS.greyBase};
  }
  thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
    padding: ${SPACERS.tiny} ${SPACERS.small};
  }
  thead tr th,
  thead tr th td {
    padding: ${SPACERS.small};
  }
  thead th:first-child {
    border-right: 1px solid black;
    padding: 0;
    position: sticky;
    left: 0;
    z-index: 2;
    &::before {
      content: '';
      width: 1px;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      box-shadow: 1px 0px 0px 0px ${COLORS.greyLight};
    }
  }
  tbody th {
    background-color: ${COLORS.whiteBase};
    position: sticky;
    left: 0;
    z-index: 1;
    display: flex;
    height: 100%;
    align-items: center;
  }
  .delete-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
`;

export interface GridTableProps<T extends TableHeaderId> {
  children?: React.ReactChild | React.ReactChildren;
  headers: GridTableRowProps<T>['headers'];
  rows: T[];
  RenderEmptyState: React.FC;
  onDelete?: (row: T, i: number) => unknown;
  onRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement>,
    row: T,
    i: number,
  ) => unknown;
}

const GridTable = <T extends TableHeaderId>({
  requestType,
  headers,
  rows,
  children,
  RenderEmptyState,
  onRowClick,
  onDelete,
}: GridTableProps<T> & { requestType: RequestState<T>['type'] }) => {
  const hasDeleteColumn = typeof onDelete === 'function';

  const modifiedHeaders = hasDeleteColumn
    ? [
        ...headers,
        {
          text: 'Delete',
          Render: (item): JSX.Element => (
            <div
              className="delete-icon"
              onClick={e => {
                e.stopPropagation();
                onDelete(item?.row, item?.index);
              }}
            >
              <DeleteIcon color="primary" />
            </div>
          ),
        },
      ]
    : headers;

  if (requestType === 'REQUEST_SUCCESS' && rows.length === 0) {
    return (
      <StyledTable>
        <RenderEmptyState />
      </StyledTable>
    );
  }
  return (
    <StyledTable>
      <thead>
        <tr>
          <th>
            {/* <td>
              <FontAwesomeIcon icon={faSquare} />
            </td> */}
            <td className="primary">{modifiedHeaders[0].text}</td>
          </th>
          {modifiedHeaders.slice(1).map(h => (
            <th key={h.text}>{h.text}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <GridTableRow
            key={`${row.id}-${i}`}
            headers={modifiedHeaders}
            row={row}
            index={i}
            onRowClick={onRowClick}
          />
        ))}
      </tbody>
      {children}
    </StyledTable>
  );
};

export default GridTable;
